import { useState } from 'react';
import {
  Button,
  Dialog,
  Group,
  IconEdit,
  IconPlusCircle,
  IconTrash,
  Table,
  Typography,
  useModalPortal
} from '@screentone/core';

import { useAlert } from 'contexts/alert/useAlert';
import { useConvertedProperty } from 'hooks';
import { SectionParameterSchemaForm } from './SectionParameterSchemaForm';
import { useSectionParameterSchemas } from './useSectionParameterSchemas';

export type SectionParameterSchemaEntry = {
  key: string;
  label: string;
  type: string;
  enumValues?: string[] | null;
  deprecated?: boolean | null;
  placeholder?: string | null;
  userExposed?: boolean | null;
};

interface SectionParameterSettingsProps {
  isReadOnly?: boolean;
}

const SectionParameterSettings = ({ isReadOnly = false }: SectionParameterSettingsProps) => {
  const currentProperty = useConvertedProperty();
  const { alertError, alertSuccess } = useAlert();
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
  const { renderNode } = useModalPortal();

  const { schemas, isLoading, error, createSchema, updateSchema, deleteSchema } = useSectionParameterSchemas(
    currentProperty ?? ''
  );

  const [editingEntry, setEditingEntry] = useState<SectionParameterSchemaEntry | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const handleAdd = () => {
    setEditingEntry(null);
    setModalOpen(true);
  };

  const handleEdit = (entry: SectionParameterSchemaEntry) => {
    setEditingEntry(entry);
    setModalOpen(true);
  };

  const handleDelete = async (key: string) => {
    if (!currentProperty) return;
    try {
      await deleteSchema(key);
      alertSuccess('Parameter deleted.');
    } catch (err) {
      alertError('Failed to delete parameter.');
    }
  };

  const handleFormSave = async (entry: SectionParameterSchemaEntry) => {
    if (!currentProperty) return;
    try {
      if (editingEntry) {
        await updateSchema(editingEntry.key, entry);
        alertSuccess('Parameter updated.');
      } else {
        await createSchema(entry);
        alertSuccess('Parameter created.');
      }
      setModalOpen(false);
      setEditingEntry(null);
    } catch (err) {
      alertError('Failed to save parameter.');
    }
  };

  const handleFormCancel = () => {
    setModalOpen(false);
    setEditingEntry(null);
  };

  if (isLoading) {
    return <Typography variant="bodytext">Loading section parameters...</Typography>;
  }

  if (error) {
    return (
      <Typography variant="bodytext" color="asphalt">
        Failed to load section parameter schemas.
      </Typography>
    );
  }

  return (
    <>
      <Typography variant="bodytext" color="asphalt">
        Define the parameters that can be configured per section. These apply to all sections for this publication.
      </Typography>
        {!isReadOnly && (
          <Group align="end" margin={{ bottom: 'sm' }}>
            <Button tertiary icon={IconPlusCircle} iconPosition="left" onClick={handleAdd}>
              Add Parameter
            </Button>
          </Group>
        )}
        {schemas.length === 0 ? (
          <Typography variant="bodytext" color="asphalt">
            No section parameters defined yet.
          </Typography>
        ) : (
          <Table fullWidth>
            <Table.Head>
              <Table.Row>
                <Table.Cell>
                  <Typography variant="note" weight="bold">
                    Key
                  </Typography>
                </Table.Cell>
                <Table.Cell>
                  <Typography variant="note" weight="bold">
                    Label
                  </Typography>
                </Table.Cell>
                <Table.Cell>
                  <Typography variant="note" weight="bold">
                    Type
                  </Typography>
                </Table.Cell>
                <Table.Cell>
                  <Typography variant="note" weight="bold">
                    Enum Values
                  </Typography>
                </Table.Cell>
                <Table.Cell>
                  <Typography variant="note" weight="bold">
                    User Exposed
                  </Typography>
                </Table.Cell>
                <Table.Cell>
                  <Typography variant="note" weight="bold">
                    Deprecated
                  </Typography>
                </Table.Cell>
                {!isReadOnly && (
                  <Table.Cell>
                    <Typography variant="note" weight="bold">
                      Actions
                    </Typography>
                  </Table.Cell>
                )}
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {schemas.map((schema) => (
                <Table.Row key={schema.key}>
                  <Table.Cell>
                    <Typography variant="note">{schema.key}</Typography>
                  </Table.Cell>
                  <Table.Cell>{schema.label}</Table.Cell>
                  <Table.Cell>
                    <Typography variant="note">{schema.type}</Typography>
                  </Table.Cell>
                  <Table.Cell>{schema.enumValues?.join(', ') ?? '—'}</Table.Cell>
                  <Table.Cell>
                    {schema.userExposed ? (
                      <Typography variant="note" color="emerald">
                        Yes
                      </Typography>
                    ) : (
                      '—'
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    {schema.deprecated ? (
                      <Typography variant="note" color="lava">
                        Yes
                      </Typography>
                    ) : (
                      '—'
                    )}
                  </Table.Cell>
                  {!isReadOnly && (
                    <Table.Cell>
                      <Group gap="sm">
                        <Button tertiary componentEl="button" icon={IconEdit} onClick={() => handleEdit(schema)} />
                        <Button
                          tertiary
                          componentEl="button"
                          icon={IconTrash}
                          color="lava"
                          onClick={() => void handleDelete(schema.key)}
                        />
                      </Group>
                    </Table.Cell>
                  )}
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}

      {modalOpen && (
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        <Dialog status="open" onDismiss={handleFormCancel} renderNode={renderNode}>
          <Dialog.Title>{editingEntry ? 'Edit Parameter' : 'Add Parameter'}</Dialog.Title>
          <Dialog.Content>
            <SectionParameterSchemaForm
              initial={editingEntry ?? undefined}
              onSave={(entry) => void handleFormSave(entry)}
              onCancel={handleFormCancel}
            />
          </Dialog.Content>
        </Dialog>
      )}
    </>
  );
};

export default SectionParameterSettings;
